import { useDatabase } from "@/hooks/useDatabase";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart3, FlaskConical, Microscope, BookOpen, TrendingUp, Award } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import ResearchTrendsChart from "@/components/charts/ResearchTrendsChart";
import SpeciesEfficacyChart from "@/components/charts/SpeciesEfficacyChart";
import BiomarkerChangesChart from "@/components/charts/BiomarkerChangesChart";
import WorldMap from "@/components/WorldMap";
import AnimatedCounter from "@/components/AnimatedCounter";
import GlobalSearch from "@/components/GlobalSearch";

export default function Dashboard() {
  const { database, loading } = useDatabase();
  const studies = database?.studies || [];
  const mushrooms = database?.mushrooms || [];
  const compounds = database?.compounds || [];
  const conditions = database?.conditions || [];
  const interventions = database?.interventions || [];
  const outcomes = database?.outcomes || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F4F2EC] via-white to-[#F4F2EC]">
        <div className="container py-12">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const highQualityStudies = studies.filter((s: any) => s.quality_score >= 4);
  const recentStudies = studies.filter((s: any) => s.year >= 2020);
  const rctStudies = studies.filter((s: any) => s.study_type === "RCT");

  const stats = [
    {
      title: "Clinical Trials",
      value: studies.length,
      description: "Comprehensive database",
      icon: Microscope,
      color: "text-[#A98A2C]",
      bgColor: "bg-[#F4F2EC]"
    },
    {
      title: "Mushroom Species",
      value: mushrooms.filter((m: any) => m.total_studies > 0).length,
      description: "With documented research",
      icon: FlaskConical,
      color: "text-[#C26A1E]",
      bgColor: "bg-[#F4F2EC]"
    },
    {
      title: "Bioactive Compounds",
      value: compounds.length,
      description: "Identified and cataloged",
      icon: BarChart3,
      color: "text-[#C8A53D]",
      bgColor: "bg-[#F4F2EC]"
    },
    {
      title: "Health Conditions",
      value: conditions.length,
      description: "Therapeutic applications",
      icon: BookOpen,
      color: "text-[#B45F3D]",
      bgColor: "bg-[#F4F2EC]"
    }
  ];

  const highlights = [
    {
      title: "High-Quality RCTs",
      value: highQualityStudies.length,
      description: "Gold-standard research (4-5 stars)",
      icon: Award,
      trend: "+12% from last year"
    },
    {
      title: "Recent Studies",
      value: recentStudies.length,
      description: "Published 2020-2025",
      icon: TrendingUp,
      trend: "86% of total database"
    },
    {
      title: "Randomized Trials",
      value: rctStudies.length,
      description: "RCT methodology",
      icon: Microscope,
      trend: "Highest evidence level"
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="border-b border-[#E4E1D8] bg-white/90 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="container py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-[#2A2A28]">Mushroom Clinical Research</h1>
              <p className="text-[#6E6B64] mt-1 font-medium">Comprehensive knowledge base and database</p>
            </div>
            <div className="flex items-center gap-4 w-full lg:w-auto">
              <div className="flex-1 lg:flex-initial lg:w-80">
                <GlobalSearch />
              </div>
              <Link href="/">
                <Button className="bg-[#A98A2C] hover:bg-[#8A6F22] text-white whitespace-nowrap">Browse Studies</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container py-12">
        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="border border-[#E4E1D8] shadow-[0_2px_8px_rgba(42, 42, 40,0.08)] hover:shadow-[0_8px_24px_rgba(42, 42, 40,0.15)] transition-all duration-300 hover:-translate-y-1 rounded-xl">
                <CardHeader className="pb-3">
                  <div className={`w-12 h-12 rounded-lg ${stat.bgColor} flex items-center justify-center mb-3`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <CardTitle className="text-3xl font-bold"><AnimatedCounter value={stat.value} /></CardTitle>
                  <CardDescription className="text-base">{stat.title}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">{stat.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Highlights */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Research Highlights</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlights.map((highlight, index) => {
              const Icon = highlight.icon;
              return (
                <Card key={index} className="border-0 shadow-md">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-2xl font-bold mb-1">{highlight.value}</CardTitle>
                        <CardDescription className="text-base font-medium">{highlight.title}</CardDescription>
                      </div>
                      <Icon className="w-5 h-5 text-[#A98A2C]" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-2">{highlight.description}</p>
                    <p className="text-xs text-[#A98A2C] font-medium">{highlight.trend}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Interactive Visualizations */}
        <div className="mb-12 animate-fade-in">
          <h2 className="text-2xl font-bold mb-6">Research Analytics</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ResearchTrendsChart 
              studies={studies} 
              interventions={interventions} 
              mushrooms={mushrooms} 
            />
            <SpeciesEfficacyChart 
              studies={studies} 
              interventions={interventions} 
              mushrooms={mushrooms} 
            />
          </div>
          <div className="grid grid-cols-1 gap-6">
            <BiomarkerChangesChart outcomes={outcomes} />
          </div>
        </div>

        {/* World Map Visualization */}
        <div className="mb-12 animate-fade-in">
          <h2 className="text-2xl font-bold mb-6">Global Research Distribution</h2>
          <WorldMap />
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Link href="/">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Microscope className="w-5 h-5 text-[#A98A2C]" />
                  Browse Clinical Trials
                </CardTitle>
                <CardDescription>
                  Search and filter {studies.length} clinical trials by species, condition, and quality
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/compounds">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FlaskConical className="w-5 h-5 text-[#C26A1E]" />
                  Compound Library
                </CardTitle>
                <CardDescription>
                  Explore {compounds.length} bioactive compounds and their therapeutic properties
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/extraction">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#A98A2C]" />
                  Extraction Science
                </CardTitle>
                <CardDescription>
                  Learn optimal extraction methods and bioavailability data
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/marketing">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#B45F3D]" />
                  Marketing Dashboard
                </CardTitle>
                <CardDescription>
                  Social media-ready statistics and content for each study
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Card className="border-0 shadow-md bg-gradient-to-br from-[#A98A2C] to-[#C26A1E] text-white col-span-full md:col-span-2">
            <CardHeader>
              <CardTitle className="text-white">Top Researched Species</CardTitle>
              <CardDescription className="text-[#F4F2EC]">
                Most comprehensive clinical trial coverage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {mushrooms
                  .filter((m: any) => m.total_studies > 0)
                  .sort((a: any, b: any) => b.total_studies - a.total_studies)
                  .slice(0, 6)
                  .map((mushroom: any) => (
                    <div key={mushroom.mushroom_id} className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <p className="font-semibold text-white">{mushroom.common_name}</p>
                      <p className="text-sm text-[#F4F2EC]">{mushroom.total_studies} studies</p>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
